import React, { useCallback, useState } from "react";
import { UserScreen } from "../layouts/user-screen.tsx";
import { Tabs } from "../components/tabs/tabs.tsx";
import { SignupUserForm } from "./sign-up/signup-user-form.tsx";
import { LoginUserForm } from "./login/login-user-form.tsx";
import { Divider } from "../components/divider/divider.tsx";
import GroupedSocialButtons from "../components/social-buttons/grouped-social-buttons.tsx";
import { FontStyles } from "../utils/font-styles.ts";
import { VerifyEmailScreen } from "./sign-up/verify-email-screen.tsx";
import { useDialogContext } from "../utils/dialog-context.tsx";
import { LoginConfigProvider } from "./utils/login-config-context.tsx";
import { useLoginDialogContext } from "./utils/login-dialog-context.tsx";
import { setPopupAlertInLocalStorage } from "../components/alerts/popup-alert.tsx";
import { isRelativeUrl } from "../utils/url-validator.ts";
import { TextLink } from "../components/text-link.tsx";

const textStyle = `font-normal ${FontStyles[`body-l`]}`;
const padding = "pt-5 px-4.5 md:px-7.5 md:pt-7.5";

export type TabId = "create-account" | "sign-in";

export const TABS: Record<TabId, { label: string; id: TabId }> = {
  "create-account": { label: "Create an account", id: "create-account" },
  "sign-in": { label: "Sign in", id: "sign-in" },
};

const HEADER_TEXT: Record<TabId, string> = {
  "create-account": "Join My ESPC",
  "sign-in": "Sign in to My ESPC",
};

export interface LoginSignupProps {
  initialTab: TabId;
}

export const UserLoginSignUpScreen: React.FC<LoginSignupProps> = (props) => {
  const [activeTab, setActiveTab] = useState<TabId>(TABS[props.initialTab].id);
  const [displayVerifyEmail, setDisplayVerifyEmail] = useState(false);
  const [email, setEmail] = useState("");
  const dialogContext = useDialogContext();
  const loginDialogContext = useLoginDialogContext();
  const onTabChange = (label: number) => {
    const selectedTab = Object.values(TABS)[label];
    setActiveTab(selectedTab.id);
    if (!dialogContext) {
      if (selectedTab.id == "create-account") {
        window.history.replaceState(null, "", "/users/register");
        document.title = "Register with My ESPC | ESPC";
      } else {
        window.history.replaceState(null, "", "/users/login");
        document.title = "Sign in to My ESPC | ESPC";
      }
    }
  };
  const headerText = HEADER_TEXT[activeTab];
  const bgImage =
    activeTab === TABS["sign-in"].id
      ? "/static/images/web-promo-pink-narrow.webp"
      : "/static/images/web-promo-blue-narrow.webp";

  const onDisplayChange = (display: boolean, email: string) => {
    setDisplayVerifyEmail(display);
    setEmail(email);
  };

  const handlePasswordLoginSuccess = useCallback(async () => {
    if (loginDialogContext !== undefined) {
      // if we're in the dialog, call callback
      await loginDialogContext.state.loginCallback?.();
      if (loginDialogContext.state.loginSuccessMessage) {
        setPopupAlertInLocalStorage({
          title: "Logged in",
          message: loginDialogContext.state.loginSuccessMessage,
          color: "green",
          style: "solid",
        });
      }
      // need to reload the page to refresh status
      window.location.reload();
    } else {
      // if not in dialog, see if there's a return url set
      // if yes, redirect to it
      // otherwise redirect to homepage
      // when NOT in dialog there's no callback to call, it can only be provided in the dialog
      const params = new URLSearchParams(window.location.search);
      const lowerCasedParams = new URLSearchParams();
      for (const [name, value] of params) {
        lowerCasedParams.append(name.toLowerCase(), value);
      }
      const returnUrl = lowerCasedParams.get("rp");

      if (returnUrl && isRelativeUrl(returnUrl)) {
        window.location.href = returnUrl;
      } else {
        window.location.href = "/";
      }
    }
  }, [loginDialogContext]);

  const handleSocialLoginSuccess = useCallback(
    async (responseStatusCode: number) => {
      if (responseStatusCode === 201) {
        // new user created
        // redirect to welcome page
        window.location.href = "/users/welcome";
      } else {
        await handlePasswordLoginSuccess();
      }
    },
    [handlePasswordLoginSuccess],
  );

  if (displayVerifyEmail) {
    return <VerifyEmailScreen email={email} />;
  } else {
    return (
      <LoginConfigProvider>
        <UserScreen image={bgImage} headerText={headerText} dismissible={false}>
          <div>
            <div className={"px-4.5 pb-5 md:p-7.5 !pt-2"}>
              <p className={textStyle}>
                Save your search preferences and favourite properties. We’ll keep you up to date with the latest
                property alerts.
              </p>
            </div>
            <Tabs
              labels={Object.values(TABS).map((tab) => tab.label)}
              onTabChange={onTabChange}
              initialTab={Object.values(TABS).findIndex((tab) => tab.id === props.initialTab) || 0}
            >
              <div className={padding}>
                <SignupUserForm displayVerifyEmail={displayVerifyEmail} onDisplayChange={onDisplayChange} />
              </div>
              <div className={padding}>
                <LoginUserForm onAuthSuccess={handlePasswordLoginSuccess} />
              </div>
            </Tabs>
            <div className={"flex flex-col gap-4.5 md:gap-7.5 pb-5 md:pb-7.5 " + padding}>
              <Divider message="or continue with" />
              <GroupedSocialButtons onAuthSuccess={handleSocialLoginSuccess} />
              <p className={`font-normal ${FontStyles[`body-s`]}`}>
                By signing in or registering you accept our{" "}
                <TextLink href="https://espc.com/espc-policies/terms-of-use">Terms of Use</TextLink> and{" "}
                <TextLink href="https://espc.com/espc-policies">Privacy Policy</TextLink>.
                {" This site is protected by reCAPTCHA and the Google "}
                <TextLink href="https://policies.google.com/privacy">Privacy Policy</TextLink> and{" "}
                <TextLink href="https://policies.google.com/terms">Terms of Service</TextLink> apply.
              </p>
            </div>
          </div>
        </UserScreen>
      </LoginConfigProvider>
    );
  }
};
